/**
 * What the menus show about progression, derived from the shared rules (docs/game-design.md
 * §2–5). No rules live here: every number comes from @caged/shared config and formulas.
 */
import {
  calcFightXp,
  dayKey,
  FIGHT,
  fighterName,
  levelFromXp,
  nextSkillPointCost,
  nextWeekResetUtc,
  PERKS,
  previousDayKey,
  PROGRESSION,
  rollover,
  SKILL_GROUPS,
  SKILLS,
  weeklyBandFactor,
  xpToNextLevel,
  type LevelInfo,
  type PerkId,
  type Progress,
  type SkillDef,
  type SkillGroup,
  type SkillRanks,
  type XpResult,
} from '@caged/shared';
import { timeZone } from '../state/store';
import { formatDecimal, formatNumber, formatPercent, round1 } from './format';

/** The progress as of now (week and day counters moved forward), without saving it. */
export function currentProgress(p: Progress, nowMs = Date.now()): Progress {
  return rollover(p, nowMs, timeZone());
}

export interface LevelView extends LevelInfo {
  /** 0–1 fill of the level bar. */
  frac: number;
  left: number;
}

export function levelView(xpTotal: number): LevelView {
  const l = levelFromXp(xpTotal);
  return { ...l, frac: l.maxed ? 1 : l.into / l.needed, left: l.maxed ? 0 : l.needed - l.into };
}

/** Total XP at the start of `level` (level 1 starts at 0). */
export function xpAtLevelStart(level: number): number {
  let sum = 0;
  for (let l = 1; l < level; l++) sum += xpToNextLevel(l);
  return sum;
}

export interface SkillBarView {
  now: number;
  need: number;
  left: number;
  frac: number;
  /** All 150 points from XP are earned: the bar now pays coins. */
  capped: boolean;
}

export function skillBarView(pointsEarned: number, barXp: number): SkillBarView {
  const need = nextSkillPointCost(pointsEarned);
  return {
    now: barXp,
    need,
    left: Math.max(0, need - barXp),
    frac: need > 0 ? Math.min(1, barXp / need) : 0,
    capped: pointsEarned >= PROGRESSION.skillPoints.maxFromXp,
  };
}

// ---------------------------------------------------------------------------------------------
// Weekly XP budget (§2.3) and rested bonus (§2.4)

/** Relative widths of the 4 band segments on the weekly meter (design: 40 / 30 / 20 / 10). */
const BAND_FLEX = [40, 30, 20, 10];

export interface BandView {
  /** "1–40", "41–100", "101–200", "201+". */
  range: string;
  pct: number;
  flex: number;
  /** 0–1 fill of this segment. */
  fill: number;
  current: boolean;
}

export interface WeeklyView {
  fights: number;
  /** Fights at full XP (40). */
  fullXpFights: number;
  bands: BandView[];
  /** Share of fight XP the next fight gets. */
  nextFactor: number;
  restedLeft: number;
  resetAt: number;
}

export function weeklyView(p: Progress, nowMs: number): WeeklyView {
  const fights = p.weekFights;
  const nextFactor = weeklyBandFactor(fights + 1);
  let prev = 0;
  const bands = PROGRESSION.weeklyBands.map((b, i) => {
    const finite = Number.isFinite(b.upTo);
    const range = finite ? `${prev + 1}–${b.upTo}` : `${prev + 1}+`;
    const fill = finite ? Math.min(1, Math.max(0, (fights - prev) / (b.upTo - prev))) : fights > prev ? 1 : 0;
    const current = fights + 1 > prev && (!finite || fights + 1 <= b.upTo);
    const view: BandView = { range, pct: Math.round(b.factor * 100), flex: BAND_FLEX[i] ?? 10, fill, current };
    prev = finite ? b.upTo : prev;
    return view;
  });
  return {
    fights,
    fullXpFights: PROGRESSION.weeklyBands[0]?.upTo ?? 40,
    bands,
    nextFactor,
    restedLeft: p.restedLeft,
    resetAt: nextWeekResetUtc(nowMs),
  };
}

// ---------------------------------------------------------------------------------------------
// Day streak (§2.1)

/** Days in a row with a fight, 0 once a day was missed. */
export function streakView(p: Progress, nowMs: number): { days: number; target: number; reward: number } {
  const today = dayKey(nowMs, timeZone());
  const alive = p.streakLastDay === today || p.streakLastDay === previousDayKey(today);
  return {
    days: alive ? p.streakDays : 0,
    target: PROGRESSION.xp.streakDays,
    reward: PROGRESSION.xp.streakReward,
  };
}

// ---------------------------------------------------------------------------------------------
// VS: XP for the coming fight (§2.5)

export interface XpPreview {
  win: number;
  loss: number;
  rested: boolean;
  vsBot: boolean;
  /** The first win of the day bonus is still available. */
  firstWin: number;
}

/** Fight XP if you win / lose the next bot fight (bots count as an even match). */
/** `league`: a league fight played live (no weekly budget, rested bonus or bot factor, §2.1). */
export function nextFightXp(p: Progress, vsBot = true, ratingDiff = 0, league = false): XpPreview {
  const base = {
    ko: false,
    ratingDiff,
    vsBot,
    fightNumberThisWeek: p.weekFights + 1,
    rested: !league && p.restedLeft > 0,
    firstWinOfDay: false,
    streakReward: false,
    league: league ? { playedLive: true } : null,
  };
  return {
    win: calcFightXp({ ...base, outcome: 'win' }).fightXp,
    loss: calcFightXp({ ...base, outcome: 'loss' }).fightXp,
    rested: base.rested,
    vsBot: vsBot && !league,
    firstWin: p.wonToday ? 0 : PROGRESSION.xp.firstWinOfDay,
  };
}

// ---------------------------------------------------------------------------------------------
// Result: the XP breakdown line by line (§2.5)

export interface XpRow {
  key: XpResult['lines'][number]['key'];
  kind: 'add' | 'mul';
  value: number;
  /** For multipliers: the running value before and after ("120 × 1.2 = 144"). */
  from?: number;
  to?: number;
  /** A bonus added after the multipliers (first win, streak): not doubled by rested. */
  bonus: boolean;
  tone: 'amber' | 'green' | 'muted';
}

const BONUS_KEYS = new Set(['firstWin', 'streak']);

export function xpRows(xp: XpResult): XpRow[] {
  let x = 0;
  return xp.lines.map((l) => {
    const bonus = BONUS_KEYS.has(l.key);
    if (l.kind === 'add') {
      if (!bonus) x += l.value;
      return { key: l.key, kind: l.kind, value: l.value, bonus, tone: 'amber' };
    }
    const from = x;
    const to = round1(x * l.value);
    x = to;
    const tone = l.key === 'rested' ? 'green' : l.key === 'opponent' && l.value > 1 ? 'amber' : 'muted';
    return { key: l.key, kind: l.kind, value: l.value, from: round1(from), to, bonus, tone };
  });
}

// ---------------------------------------------------------------------------------------------
// Skills (§5.2) and perks (§5.4)

/**
 * The effect value at `rank`, as shown on a skill row, in the current language: "+10.5%", "−9%",
 * or the fight's own number at that rank (D48): the jab's damage "76", the slam's "134",
 * a slam's on you "106" (D49), max health "3,299". Computed as the simulation does.
 */
export function skillEffect(def: SkillDef, rank: number): string {
  switch (def.display) {
    case 'percent':
      return formatPercent(def.perRank * rank, true);
    case 'negativePercent':
      return formatPercent(-def.perRank * rank, true);
    case 'jab':
      return formatNumber(Math.round(FIGHT.strike.damage * (1 + def.perRank * rank)));
    case 'slam':
      return formatNumber(Math.round(FIGHT.grapple.damage * (1 + def.perRank * rank)));
    case 'slamTaken':
      return formatNumber(Math.round(FIGHT.grapple.damage * (1 - def.perRank * rank)));
    case 'health':
      return formatNumber(Math.round(FIGHT.maxHealth * (1 + def.perRank * rank)));
  }
}

export function skillsInGroup(group: SkillGroup): SkillDef[] {
  return SKILLS.filter((s) => s.group === group);
}

/** Numbers for the perk descriptions, straight from config so tuning updates the copy. */
/** A perk's numbers for its text, formatted in the current language (0.08 s → "0,08" in German). */
export function perkParams(id: PerkId): Record<string, string> {
  return Object.fromEntries(Object.entries(perkNumbers(id)).map(([k, v]) => [k, formatDecimal(v)]));
}

function perkNumbers(id: PerkId): Record<string, number> {
  const pct = (v: number) => Math.round(v * 100);
  switch (id) {
    case 'heavyHands':
      return { pct: pct(PERKS.heavyHands.strikeKnockbackMul - 1) };
    case 'guardCrusher':
      return { stamina: PERKS.guardCrusher.blockedStrikeStaminaCost };
    case 'feint':
      return { s: PERKS.feint.window };
    case 'oneTwo':
      return { s: PERKS.oneTwo.window };
    case 'jabAndMove':
      return { pct: pct(PERKS.jabAndMove.attackSpeedMul), base: pct(FIGHT.attackSpeedMul) };
    case 'fenceSlam':
      return { pct: pct(PERKS.fenceSlam.damageMul - 1) };
    case 'standUp':
      return { s: PERKS.standUp.grappleImmunity };
    case 'parry':
      return { s: PERKS.parry.window };
    case 'ironGuard':
      return { pct: pct(PERKS.ironGuard.signatureReduction) };
    case 'tough':
      return { pct: pct(1 - PERKS.tough.strikeStunMul) };
    case 'ironChin':
      return { hp: PERKS.ironChin.leaveHealth };
    case 'lightFeet':
      return { pct: pct(PERKS.lightFeet.blockSpeedMul), base: pct(FIGHT.blockSpeedMul) };
    case 'cageEscape':
      return { pct: pct(PERKS.cageEscape.speedMul - 1) };
    case 'secondBreath':
      return { hp: PERKS.secondBreath.healthBetweenRounds };
    case 'workhorse':
      return { pct: pct(1 - PERKS.workhorse.costMul) };
    case 'headStart':
      return { meter: PERKS.headStart.meter };
    case 'quickRelease':
      return { from: FIGHT.signature.windup, to: PERKS.quickRelease.windup };
    default:
      return {};
  }
}

/** Average rank × 10 per skill group, 0–100 (the VS skill bars). */
export function groupStrength(ranks: SkillRanks): Record<SkillGroup, number> {
  const out = { striking: 0, grappling: 0, defense: 0, engine: 0 } as Record<SkillGroup, number>;
  for (const g of SKILL_GROUPS) {
    const list = skillsInGroup(g);
    const sum = list.reduce((a, s) => a + ranks[s.id], 0);
    out[g] = list.length ? (sum / list.length) * 10 : 0;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Guest fighter names (the design's "SoggyWalrus42")

export const NAME_MAX = 16;

export function randomFighterName(): string {
  return fighterName(Math.random);
}

/** Fight format "3 × 60 s". */
export const FORMAT = { rounds: FIGHT.rounds, seconds: FIGHT.roundSeconds };
